//@ts-check

/**
 * Event System Module
 *
 * Unified event handling for:
 * - Skill-check events (from event definitions)
 * - Bonus loot events (conditional on skill event success)
 * - Fishing events (water rooms only)
 *
 * All events are scheduled and processed through this module.
 * Integrates with raid.js for room progression timing.
 */

import * as config from "../constants.js";
import { gameState, saveGameState } from "./gameState.js";
import { updateUI } from "./game.js";
import { addNotification } from "./notifications.js";
import { getTechniqueName, getItemName, getSkillDisplayName } from "./util.js";
import { debug, isDebug, CH } from './debugLog.js';
// Import pure game logic from eventsystem-core (no browser dependencies)
import { initializeRoomEvents, cleanupRoomEvents, getEventsSummary, getEventsForRoom,
  scheduleSkillCheckEvents, selectEventVariant, resolveSkillCheckOutcome,
  generateSkillEventLootPool, applySkillEventDamage, shouldTriggerBonusLoot,
  generateBonusLoot, cleanupBonusLootEvent, logEventProblem, processScheduledEventsCore,
  processSkillCheckEvent, processBonusLootEvent, setEventPresenters, resetEventPresenters, awardFishLoot
} from "./eventSystem-core.js"
// Export the same for backward compatability
export { initializeRoomEvents, cleanupRoomEvents, getEventsSummary, processSkillCheckEvent, logEventProblem }

/**
 * Register the clickable, browser-only event presenters so bonus-loot and
 * fishing events spawn a collectible the player must click. Called once from
 * raid.js at raid start; headless (simulator/tests) never calls this and keeps
 * the auto-grant default.
 */
export function installBrowserEventPresenters() {
  setEventPresenters({ bonusLoot: spawnBonusLootEvent, fishing: spawnFish });
}

/**
 * Pick presenters for the raid about to start.
 *
 * Hands-off raids take the headless AUTO-GRANT path: nobody is watching to
 * click a diamond or a fish, so the clickable collectible would simply expire
 * and take its loot with it — and the sticky "Bonus loot! …" toast would nag
 * about an offer the player never agreed to be responsible for.
 *
 * Fishing goes with it for the same reason, not just bonus loot: it is the
 * same clickable-collectible mechanic, and leaving it on would make automation
 * quietly lose fish loot.
 *
 * Chosen per raid because automation is a toggle — a raid started by hand still
 * gets the collectibles even if the player arms automation mid-run.
 */
export function selectEventPresentersForRaid({ automated }) {
  if (automated) resetEventPresenters();
  else installBrowserEventPresenters();
}

// ============================================================================
// Event Initialization
// ============================================================================

/**
 * Process all scheduled events for the current tick
 *
 * ⚠ NOT LIVE. The only call site is commented out (raid-core.js ~:1906); the
 * real path is raid-core's own loop, which dispatches through
 * presentBonusLootEvent / presentFishingEvent. This copy spawns the clickable
 * presenters DIRECTLY, so it also bypasses selectEventPresentersForRaid() and
 * would hand a hands-off raid a diamond nobody is there to click. Fix bugs in
 * raid-core's loop, not here — and if this is ever re-enabled, route it through
 * the presenters first.
 *
 * @param {Object} state - gameState object
 * @param {Array} team - Current raid team for skill checks
 */
export function processScheduledEvents(state, team) {
  if (!state.currentRaid?.raidInProgress?.events) {
    return;
  }
  
  const raid = state.currentRaid.raidInProgress;
  const pathStep = state.currentRaid.selectedPath[raid.currentRoomIndex];

  const eventInfo = processScheduledEventsCore(state, team);
  if (eventInfo?.eventLifeCycle == 'start') {
    switch (eventInfo.eventType) {
      case 'skillCheck':
        logEventProblem(eventInfo.eventData, raid.currentRoomIndex, state, team);
        break;
      case 'bonusLoot':
        spawnBonusLootEvent(state);
        break;
      case 'fishing':
        spawnFish(state);
        break;
    }
  }
  else if (eventInfo?.eventLifeCycle == 'end') {
    switch (eventInfo.eventType) {
      case 'skillCheck':
        processSkillCheckEvent(eventInfo.eventData, state, pathStep.room, team);
        break;
    }
  }
}


// ============================================================================
// BONUS LOOT EVENT (CONDITIONAL)
// ============================================================================

/**
 * Spawn bonus loot event when it triggers
 * Creates clickable icon on grid that players must click to claim bonus
 * @param {Object} state - gameState object
 */
function spawnBonusLootEvent(state) {
  const bonusEvent = state.currentRaid.raidInProgress.events.bonusLootEvent;
  const currentRoomIndex = state.currentRaid.raidInProgress.currentRoomIndex;

  // Verify we're still in the right room
  if (currentRoomIndex !== bonusEvent.roomIndex) {
    bonusEvent.scheduled = false;
    return;
  }

  // Find the current room cell
  const pathStep = state.currentRaid.selectedPath[currentRoomIndex];
  const cell = document.querySelector(`[data-row="${pathStep.row}"][data-col="${pathStep.col}"]`);

  if (!cell) {
    console.error('Could not find current room cell for bonus event');
    bonusEvent.scheduled = false;
    return;
  }

  // Create bonus event target element (diamond icon)
  const target = document.createElement('div');
  target.className = 'loot-event-target';
  target.innerHTML = '💎';
  target.title = 'Click for bonus loot!';

  // Position randomly within cell bounds
  const margin = 25; // Percentage from edge
  const leftPercent = margin + Math.random() * (100 - 2 * margin);
  const topPercent = margin + Math.random() * (100 - 2 * margin);
  target.style.left = `${leftPercent}%`;
  target.style.top = `${topPercent}%`;

  // Add click handler
  target.addEventListener('click', (e) => {
    e.stopPropagation();
    handleBonusLootClick(state);
  });

  // Add to cell
  cell.appendChild(target);

  // Update state
  bonusEvent.active = true;
  bonusEvent.scheduled = false;
  bonusEvent.triggered = true;
  bonusEvent.element = target;

  // Play sound effect
  const audio = new Audio('./sounds/chime.mp3');
  audio.volume = 0.3;
  audio.play().catch(() => {}); // Ignore if autoplay blocked

  // Notify user with persistent toast
  const roomEndTime = state.currentRaid.raidInProgress.nextRoomTime;
  showBonusLootToast(roomEndTime);

  debug(CH.events, `Bonus loot event spawned in room ${currentRoomIndex}`);
}

// The live bonus-loot toast, or null. Module-level so the click handler can
// retract it the moment the loot is collected.
let bonusToast = null;

/**
 * Announce a bonus loot event, counting down the window to collect it.
 *
 * Used to be a hand-rolled `#bonus-event-toast` pinned to the opposite corner
 * from every other notification. It is now a sticky toast in the shared row:
 * sticky because a 5s fade is exactly how a timed opportunity gets missed, and
 * shared because there was no reason for this one message to have its own
 * widget beyond sticky support not existing yet.
 *
 * Sticky here still means "does not fade on a timer", NOT "stays forever" — the
 * offer expires with the room, and a toast still advertising it afterwards
 * would be worse than one that faded. It retracts on collect, at the countdown,
 * and if the event goes inactive for any other reason.
 *
 * @param {Number} roomEndTime - epoch ms when the room (and the offer) ends
 */
function showBonusLootToast(roomEndTime) {
  bonusToast?.dismiss(); // never stack two

  const secondsLeft = () => Math.max(0, Math.ceil((roomEndTime - Date.now()) / 1000));
  const label = (secs) => `💎 Bonus loot! Click the 💎 in the room — ${secs}s left`;

  bonusToast = addNotification(label(secondsLeft()), 'warning', { sticky: true });
  if (!bonusToast) return; // no container

  const handle = bonusToast;
  const tick = () => {
    if (handle !== bonusToast) return; // superseded by a newer event

    // Watch the event itself, not just the clock. cleanupRoomEvents() clears
    // `active` when the room ends or the raid is abandoned, and that lives in
    // headless core where it cannot reach the DOM — so the toast follows the
    // flag rather than needing a callback plumbed out of core.
    const offer = gameState.currentRaid?.raidInProgress?.events?.bonusLootEvent;
    const secs = secondsLeft();
    if (secs <= 0 || !offer?.active) {
      handle.dismiss();
      bonusToast = null;
      return;
    }
    handle.update(label(secs));
    setTimeout(tick, 1000);
  };
  setTimeout(tick, 1000);
}

/**
 * Handle click on bonus loot target
 * Distributes cascading bonus loot and cleans up
 * @param {Object} state - gameState object
 */
function handleBonusLootClick(state) {
  const bonusEvent = state.currentRaid.raidInProgress.events.bonusLootEvent;

  if (!bonusEvent.active || !bonusEvent.element) {
    return;
  }

  // Distribute bonus loot (starting with common rarity, cascades up)
  processBonusLootEvent(state, 'common');

  // Clean up the event
  cleanupBonusLootEvent(state);

  // Retract the offer immediately rather than waiting for the next countdown
  // tick to notice the event went inactive.
  bonusToast?.dismiss();
  bonusToast = null;

  // Update UI
  updateUI();
  saveGameState();

  debug(CH.events, 'Bonus loot event clicked and distributed');
}

// ============================================================================
// FISHING EVENT (WATER ROOMS)
// ============================================================================

/**
 * Spawn a fish target in the room for player to click
 * Called when fishing timer triggers
 * @param {Object} state - gameState object
 */
function spawnFish(state) {
  const raid = state.currentRaid.raidInProgress;
  const fishingEvent = raid.events.fishingEvent;

  // Don't spawn if already active or room changed
  if (fishingEvent.targetActive || fishingEvent.roomIndex !== raid.currentRoomIndex) {
    return;
  }

  // Get the cell
  const pathStep = state.currentRaid.selectedPath[fishingEvent.roomIndex];
  const cell = document.querySelector(`[data-row="${pathStep.row}"][data-col="${pathStep.col}"]`);
  if (!cell) return;

  // Create fish target element
  const fish = ['🐟', '🐠', '🦞', '🦀'];
  const target = document.createElement('div');
  target.className = 'loot-event-target';
  target.innerHTML = fish[Math.floor(Math.random() * 4)];
  target.title = 'Click to catch fish!';

  // Position randomly within cell bounds
  const margin = 15;
  const leftPercent = margin + Math.random() * (100 - 2 * margin);
  const topPercent = margin + Math.random() * (100 - 2 * margin);

  target.style.left = `${leftPercent}%`;
  target.style.top = `${topPercent}%`;

  // Add click handler
  target.addEventListener('click', (e) => {
    e.stopPropagation();
    catchFish(state);
  });

  // Add to cell
  cell.appendChild(target);

  // Update state
  fishingEvent.targetActive = true;
  fishingEvent.element = target;

  // Play sound effect
  const audio = new Audio('./sounds/fish.mp3');
  audio.volume = 0.3;
  audio.play().catch(() => {});

  debug(CH.events, 'Fish spawned for catching');
}

/**
 * Handle fish catch when player clicks fish
 * Awards Fish item and schedules next fish
 * @param {Object} state - gameState object
 */
function catchFish(state) {
  const raid = state.currentRaid.raidInProgress;
  const fishingEvent = raid.events.fishingEvent;

  if (!fishingEvent.targetActive) return;

  // Remove the target
  if (fishingEvent.element) {
    fishingEvent.element.remove();
    fishingEvent.element = null;
  }

  // Award the catch (shared with the headless auto-catch path)
  awardFishLoot(state, fishingEvent.roomIndex);

  fishingEvent.count++;
  fishingEvent.targetActive = false;
  fishingEvent.nextFishTime = Date.now() + 15000; // Next fish in 15 seconds

  debug(CH.events, `Fish caught (count: ${fishingEvent.count}):`, fishLoot);
  saveGameState();
}

// ============================================================================
// EXPORTED FUNCTIONS SUMMARY
// ============================================================================
// Public API for raid.js integration:
// - initializeRoomEvents(room, state) - Called when entering room
// - processScheduledEvents(state, team) - Called each tick in main loop
// - cleanupRoomEvents(state) - Called when room completes
// - getEventsSummary(state) - For UI/debugging
//
// Internal functions (private to this module):
// - Skill-check events: getEventsForRoom, scheduleSkillCheckEvents, selectEventVariant,
//   resolveSkillCheckOutcome, generateSkillEventLootPool, applySkillEventDamage,
//   processSkillCheckEvent
// - Bonus loot: shouldTriggerBonusLoot, scheduleConditionalBonusLoot, spawnBonusLootEvent,
//   showBonusLootToast, handleBonusLootClick, processBonusLootEvent, generateBonusLoot,
//   getItemPoolForRarity, cleanupBonusLootEvent
// - Fishing: initializeFishingEvent, spawnFish, catchFish, cleanupFishingEvent
